import React, { useContext, useState, useEffect } from "react"
import _ from "lodash"
import { Link, useNavigate } from "react-router-dom"
import { useQuery } from "@apollo/client"
import PageHeader from "../../components/PageHeader"
import { LoaderMedium } from "../../components/Loaders"
import StatusChangeDialog from "./components/StatusChangeDialog"
import { UserContext } from "../../context/userContext"
import { GET_ENTERPRISES } from "./queries"
import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"
import styles from "../../styles/Listing.module.css"

const ID_TEMPLATE = "enterprise"

export default function EnterprisesList() {
  const navigate = useNavigate()
  const userCtx = useContext(UserContext)
  const [enterprises, setEnterprises] = useState([])
  const [loadingPage, setLoadingPage] = useState(true)

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    clearGlobalFilter,
    onFilterChange,
    clearAllFilters,
  } = useFilters()

  const { data, loading: queryLoading } = useQuery(GET_ENTERPRISES, {
    variables: {
      staff_id: userCtx?.session.user.staff_id,
      enterprise_id: userCtx?.session.user.enterprise_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (data) {
      setEnterprises(
        _.map(data.enterprises, (enterprise) => ({
          ...enterprise,
          enterprise_name_display: _.unescape(enterprise.enterprise_name),
          status: enterprise.enterprise_status,
          current_organizations: enterprise.current_organizations,
          current_staff_users: enterprise.current_staff_users,
          edit: (
            <Link
              onClick={async () => {
                await userCtx.changeOrganization(enterprise.enterprise_id)
                return navigate(
                  `/form/${ID_TEMPLATE}/1/${enterprise?.enterprise_id_stamp}`
                )
              }}
              className="btn"
            >
              Edit
            </Link>
          ),
        }))
      )
      setLoadingPage(false)
    }
  }, [data, userCtx])

  const filterConfig = [
    {
      label: "Status",
      value: filters?.status?.value || "",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "ACTIVE" },
        { label: "Inactive", value: "INACTIVE" },
      ],
      onChange: (e) => onFilterChange("status", e.value),
      onClear: () => onFilterChange("status", null),
      placeholder: "Select Status",
      filter: false,
    },
  ]

  if (loadingPage || queryLoading) return <LoaderMedium />

  const columns = [
    {
      field: "enterprise_name",
      header: "Enterprise",
      sortable: true,
      body: (rowData) => (
        <Link
          to="/enterprise/library"
          onClick={(e) => {
            e.preventDefault()
            userCtx.changeOrganization(rowData.enterprise_id).then(() => {
              navigate("/enterprise/library")
            })
          }}
          className={styles.links}
        >
          {rowData.enterprise_name_display}
        </Link>
      ),
    },
    {
      field: "current_organizations",
      header: "Organizations",
      sortable: true,
      body: (rowData) => (
        <Link
          to="/enterprise/organizations"
          onClick={(e) => {
            e.preventDefault()
            userCtx.changeOrganization(rowData.enterprise_id).then(() => {
              navigate("/enterprise/organizations")
            })
          }}
          className={styles.links}
        >
          {rowData.current_organizations}
        </Link>
      ),
    },
    {
      field: "current_locations",
      header: "Locations",
      sortable: true,
      body: (rowData) => (
        <Link
          to="/enterprise/locations"
          onClick={(e) => {
            e.preventDefault()
            userCtx.changeOrganization(rowData.enterprise_id).then(() => {
              navigate("/enterprise/locations")
            })
          }}
          className={styles.links}
        >
          {rowData.current_locations}
        </Link>
      ),
    },
    {
      field: "current_staff_users",
      header: "Staff Members",
      sortable: true,
      body: (rowData) => (
        <Link
          to="/enterprise/staff"
          onClick={(e) => {
            e.preventDefault()
            userCtx.changeOrganization(rowData.enterprise_id).then(() => {
              navigate("/enterprise/staff")
            })
          }}
          className={styles.links}
        >
          {rowData.current_staff_users}
        </Link>
      ),
    },
    { field: "current_participants", header: "Participants", sortable: true },
    {
      field: "status",
      header: "Status",
      sortable: true,
      style: { width: 220 },
      body: (rowData) => <StatusChangeDialog data={rowData} />,
    },
    { field: "edit", header: "Edit" },
  ]

  return (
    <div className="main-content">
      <PageHeader
        title="Manage Enterprise"
        actions={[
          {
            to: "/form/enterprise/1",
            label: "Create new Enterprise",
          },
        ]}
      />
      <GenericTable
        data={enterprises}
        columns={columns}
        filters={filters}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={onGlobalFilterChange}
        header={
          <FilterHeader
            filters={filterConfig}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            clearGlobalFilter={clearGlobalFilter}
            clearAllFilters={clearAllFilters}
          />
        }
        paginator
        rows={50}
        sortField="enterprise_name"
        sortOrder={1}
      />
    </div>
  )
}
